/**
 * 10.2 cost of equity (scoring.valuePrice@2.0): the capital record K (E1), the premia table (E2) and COE (E3, E4). Visible facts only (R3):
 * nothing here reads `camels` or an MOU.
 *   10.2 E1, E1′  K = S_n ÷ n if horizonFinal or a quarter-end month; CET1/RWA if n = 0; else (S_n + CET1/RWA) ÷ (n + 1)
 *   10.2 E2  π_k, one row each (bp): standing rows (capital, CRE, wholesale, uninsured, rate risk) and event rows (dividend, enforcement, cards)
 *   10.2 E3  COE_s = c₀ + min(Σ standing, π_tot) ÷ 10⁴
 *   10.2 E4  π_ev = max(0, min(Σ event, π_tot − min(Σ standing, π_tot)))
 */
import { PREMIUM_KINDS, type BankState, type ContentParams, type ValuationBreakdown, type ValuationInput } from "@bsim/domain";
import { decayedAdjustment } from "./adjustments";
import { capitalRatios } from "./ratios";

type ValuationParams = ContentParams["valuation"];
export type PremiaBp = { -readonly [K in keyof ValuationBreakdown["premiaBp"]]: number };

/** The premium rows in 10.2 E2 order. */
export const PREMIUM_ROWS = ["capital", "creConcentration", "wholesale", "uninsured", "eve", "dividend", "enforcement", "event"] as const satisfies readonly (keyof PremiaBp)[];

/** Months per quarter: a quarter-end month is a multiple of it (0 = SimStart). */
const QUARTER_MONTHS = 3;
const PP = 100;

/**
 * 10.2 E1′ (OR-30.84): what a quarter-end counts in the capital record, the lower of the CET1 ratio and the target in force plus the headroom h
 * (`valuation.premia.capital.targetHeadroomRate`, a pin until a typed field; the standing target stands in where the bank carries none). The two numbers travel on the
 * capital params the engine hands the price; absent h = no cap = the plain ratio.
 */
export type CapitalCountParams = ValuationParams["premia"]["capital"] & { readonly targetHeadroomRate?: number; readonly standingTargetRate?: number };
export function countedCapRate(bank: Pick<BankState, "cet1TargetRate">, p: Pick<CapitalCountParams, "targetHeadroomRate" | "standingTargetRate"> | undefined): number | undefined {
  const target = bank.cet1TargetRate ?? p?.standingTargetRate;
  return p?.targetHeadroomRate === undefined || target === undefined ? undefined : target + p.targetHeadroomRate;
}
export const countedCet1Rate = (cet1Rate: number, capRate: number | undefined): number => (capRate === undefined ? cet1Rate : Math.min(cet1Rate, capRate));

/**
 * 10.2 E1′ (the counted record K′; E1 when no cap is carried): the mean of the counted quarter-ends, `sumCountedQuarterEndRate` where the bank has one, else the plain sum (a bank
 * written before CB13); the current month counts at the cap too. The plain mean stays with the CRO memo and I34 (`sumQuarterEndCet1Rate`).
 */
export function capitalRecordRate(bank: BankState, horizonFinal: boolean, capital?: CapitalCountParams): number {
  const { sumQuarterEndCet1Rate, sumCountedQuarterEndRate, quarterEnds: n } = bank.capitalRecord;
  const sum = sumCountedQuarterEndRate ?? sumQuarterEndCet1Rate;
  const current = countedCet1Rate(capitalRatios(bank).cet1Rate, countedCapRate(bank, capital));
  if (n === 0) return current;
  if (horizonFinal || bank.month % QUARTER_MONTHS === 0) return sum / n;
  return (sum + current) / (n + 1);
}

/** The K the capital row reads: the capital record, or (scoring unit tests only) the point-in-time CET1. */
export const capitalKRate = (bank: BankState, params: ValuationParams, horizonFinal: boolean): number =>
  params.premia.capital.measure === "pointInTime" ? capitalRatios(bank).cet1Rate : capitalRecordRate(bank, horizonFinal, params.premia.capital);

/** 10.2 E2 capital row, bp: a₁·max(0, T₁ − K)·100 + a₂·max(0, T₂ − K)·100 (K in rate, the slopes are bp per pp). */
export function capitalPremiumBp(kRate: number, capital: ValuationParams["premia"]["capital"]): number {
  return (capital.slope1BpPerPp * Math.max(0, capital.threshold1Rate - kRate) + capital.slope2BpPerPp * Math.max(0, capital.threshold2Rate - kRate)) * PP;
}

/**
 * 08.2 E2a, 10.2 E2: the cost-of-equity premium of a dividend cut (bp) in the bank's month: `openedBp` in the month it opened, falling evenly to 0 in the last
 * month of the `premiumQuarters`-th quarter (π₀ · (11 − k) ÷ 11 for k months after, with four quarters). Absent or `premiumQuarters` unknown = 0.
 */
export function dividendPremiumBp(premium: BankState["dividendPremium"], month: number, premiumQuarters: number | undefined): number {
  if (premium === undefined || premiumQuarters === undefined) return 0;
  const last = QUARTER_MONTHS * premiumQuarters - 1;
  return premium.openedBp * Math.max(0, (last - (month - premium.openedMonth)) / last);
}

/** 10.2 E2 enforcement row: a consent order or forced recapitalization is public, so it is priced in full while open and fades over `enforcementFadeQuarters` quarters once it closes. */
export function enforcementPremiumBp(bank: BankState, params: ValuationParams): number {
  const { premiaBp, enforcementFadeQuarters } = params.priceModel;
  const { status } = bank.supervisory;
  if (status === "consentOrder" || status === "forcedRecap") return premiaBp.enforcement;
  if (bank.enforcementClosedMonth === undefined) return 0;
  return premiaBp.enforcement * Math.max(0, 1 - (bank.month - bank.enforcementClosedMonth) / (QUARTER_MONTHS * enforcementFadeQuarters));
}

/**
 * 10.2 E2. A KPI the bank does not carry reads 0 (a test fixture that omits creToTrbc or wholesaleShare; the engine writes both each
 * month-end) — both sit below their thresholds at SimStart (2.274 vs 3.0; 8.92% = threshold). `held` carries the rows the caller holds at
 * their quarter-end reading (the engine reads EVE and the uninsured share only then); absent, they are priced off the bank (rate risk off `irr.eve200Rate`, capped at `rateRiskCap`).
 */
/**
 * 10.2 E2a (OR-30.67): the liquidity cushion priced in the wholesale row, b_L · max(0, L_ref − L) ÷ 0.10. The two numbers are `valuation.premia.wholesale.liquidityRefRate` and `.liquidityBpPer10Pp`;
 * the typed fields wait for contract batch 13, so the engine lays the `ContentParams.pins` values of those names onto the wholesale params it hands the price (absent = 0 = no term). A bank that
 * does not carry the ratio reads no term.
 */
export type WholesalePremiumParams = ValuationParams["premia"]["wholesale"] & { readonly liquidityRefRate?: number; readonly liquidityBpPer10Pp?: number };
export function liquidityCushionBp(lcrLite: number | undefined, p: WholesalePremiumParams): number {
  if (lcrLite === undefined || p.liquidityRefRate === undefined || p.liquidityBpPer10Pp === undefined) return 0;
  return p.liquidityBpPer10Pp * Math.max(0, p.liquidityRefRate - lcrLite) / 0.10;
}

export function premiaBp(bank: BankState, params: ValuationParams, horizonFinal: boolean, dividendPremiumQuarters?: number, held?: ValuationInput["heldPremiaBp"]): PremiaBp {
  const p = params.premia;
  const kpi = bank.kpis;
  const kRate = capitalKRate(bank, params, horizonFinal);
  return {
    capital: capitalPremiumBp(kRate, p.capital),
    creConcentration: p.creConcentration.bpPer25Pp * Math.max(0, (kpi.creToTrbc ?? 0) - p.creConcentration.thresholdRate) / 0.25,
    wholesale: p.wholesale.bpPer5Pp * Math.max(0, (kpi.wholesaleShare ?? 0) - p.wholesale.thresholdRate) / 0.05 + liquidityCushionBp(kpi.lcrLite, p.wholesale),
    uninsured: held?.uninsured ?? p.uninsured.bpPer5Pp * Math.max(0, (kpi.uninsuredShare ?? 0) - p.uninsured.thresholdRate) / 0.05,
    eve: held?.eve ?? Math.min(params.priceModel.premiaBp.rateRiskCap, p.eve.bpPerPp * Math.max(0, -bank.irr.eve200Rate - p.eve.thresholdRate) * PP),
    dividend: dividendPremiumBp(bank.dividendPremium, bank.month, dividendPremiumQuarters),
    enforcement: enforcementPremiumBp(bank, params),
    event: decayedAdjustment(bank, "coePremiumBp"),
  };
}

/** 10.2 E3–E4: the standing cost of equity (the fair multiple's), and the event premia the price charges at once, after the total cap (the events give way first). */
export function coeOf(params: ValuationParams, premia: Readonly<PremiaBp>): { readonly standingRate: number; readonly eventBp: number; readonly rate: number } {
  const cap = params.priceModel.premiaBp.totalCap;
  let standingBp = 0, eventBp = 0;
  for (const row of PREMIUM_ROWS) if (PREMIUM_KINDS[row] === "standing") standingBp += premia[row]; else eventBp += premia[row];
  const standingCappedBp = Math.min(standingBp, cap);
  const eventCappedBp = Math.max(0, Math.min(eventBp, cap - standingCappedBp));
  const standingRate = params.priceModel.coeInterceptRate + standingCappedBp / 1e4;
  return { standingRate, eventBp: eventCappedBp, rate: standingRate + eventCappedBp / 1e4 };
}
